import { useEffect, useState } from "react";
import { ImageOff, ArrowLeftRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "../lib/site";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [showBefore, setShowBefore] = useState(false);

  useEffect(() => {
    api.get("/gallery")
      .then((res) => setItems(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Hide the section entirely if there's nothing to show — keeps a fresh site clean.
  // (Disabled — section now always renders so the navbar #gallery link always works.)
  // if (!loading && items.length === 0) return null;

  return (
    <section id="gallery" className="relative py-24 sm:py-32 border-t border-white/5 bg-[#0A0A0A]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="mb-12">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
            [ 04 ] · REPAIR LOG
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Real repairs. <span className="text-[#0055FF]">Real proof.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            Snapshots from the workbench — actual jobs that came in, what got done, and the result.
          </p>
        </div>

        {items.length === 0 ? (
          <div className="bg-[#121212] border border-zinc-800 rounded-md p-16 text-center">
            <ImageOff className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-300 font-display text-lg mb-1">First repair photos coming soon.</p>
            <p className="text-zinc-500 text-sm">
              Just opened the shop — once a few jobs are done, this is where the proof shows up.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
            {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { setOpen(it); setShowBefore(false); }}
              className="group relative aspect-square overflow-hidden rounded-md border border-zinc-800 hover:border-[#0055FF] transition-colors"
              data-testid={`gallery-item-${it.id}`}
            >
              <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {it.before_image && (
                <span className="absolute top-2 right-2 bg-black/80 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded-sm text-[#FFCC00] flex items-center gap-1">
                  <ArrowLeftRight className="w-3 h-3" /> B / A
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4">
                <div className="font-display font-semibold text-sm leading-tight">{it.title}</div>
                {it.device && (
                  <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1">
                    {it.device}
                  </div>
                )}
              </div>
            </button>
          ))}
          </div>
        )}
      </div>

      {/* Detail / lightbox dialog */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-3xl p-0 overflow-hidden" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{open?.title || "Repair"}</DialogTitle>
          <DialogDescription className="sr-only">{open?.caption || ""}</DialogDescription>
          {open && (
            <div>
              <div className="relative bg-black">
                <img
                  src={showBefore && open.before_image ? open.before_image : open.image}
                  alt={open.title}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
                {open.before_image && (
                  <button
                    type="button"
                    onClick={() => setShowBefore((s) => !s)}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur border border-zinc-700 text-white font-mono text-xs tracking-[0.15em] uppercase px-4 py-2 rounded-full hover:bg-[#0055FF] transition-colors"
                    data-testid="gallery-toggle-before"
                  >
                    <ArrowLeftRight className="w-3 h-3 inline mr-2" />
                    {showBefore ? "Show after" : "Show before"}
                  </button>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <h3 className="font-display font-bold text-2xl tracking-tight">{open.title}</h3>
                  {open.device && (
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#FFCC00]">
                      {open.device}
                    </span>
                  )}
                </div>
                {open.caption && <p className="text-zinc-400 leading-relaxed">{open.caption}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Empty-state for placeholder usage (currently unused since section hides itself)
export function GalleryEmpty() {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-md p-12 text-center text-zinc-500">
      <ImageOff className="w-8 h-8 text-zinc-700 mx-auto mb-4" />
      Nothing posted yet.
    </div>
  );
}
